import { CSSTransition, TransitionGroup } from "react-transition-group";
import { styled } from "@mui/material/styles";
import { cloneElement, useRef, type ReactElement } from "react";
import type { CSSTransitionProps } from "react-transition-group/CSSTransition";
import type { FCWithChildren } from "../../api-client/resources/types/sharedTypes";

type CSSTransitionWithRefProps = CSSTransitionProps & {
	// intended behavior: https://github.com/facebook/react/issues/31824
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	children: ReactElement<any>;
};

// Fix to make CSSTransition work with React 19 since findDOMNode has been removed
// Copied from https://github.com/reactjs/react-transition-group/issues/918
const CSSTransitionWithRef = ({ children, ...restProps }: CSSTransitionWithRefProps) => {
	const ref = useRef<HTMLElement>(null);

	const setRef = (value: HTMLElement | null) => {
		ref.current = value;
	};

	return (
		<CSSTransition nodeRef={ref} {...restProps}>
			{cloneElement(children, { ref: setRef })}
		</CSSTransition>
	);
};

// see https://css-tricks.com/using-css-transitions-auto-dimensions/ for why max-height is animated instead of height
const SlideTop = styled(CSSTransitionWithRef)`
	&.${(props) => `${props.classNames as string}-enter`} {
		max-height: 0;
		overflow: hidden;
	}
	&.${(props) => `${props.classNames as string}-enter-active`} {
		max-height: 1000px;
		transition: all ${(props) => props.timeout}ms ease-in;
	}
	&.${(props) => `${props.classNames?.toString()}-exit`} {
		overflow: hidden;
		max-height: 1000px;
	}
	&.${(props) => `${props.classNames?.toString()}-exit`}.${(props) =>
			`${props.classNames?.toString()}-exit-active`} {
		max-height: 0;
		transition: all ${(props) => props.timeout}ms ease-in;
	}
` as unknown as typeof CSSTransition;

type SlideInProps = {
	transitionOn: boolean;
	timeout?: number;
};

const SlideIn: FCWithChildren<SlideInProps> = ({ transitionOn, children, timeout = 250 }) => {
	return (
		<TransitionGroup>
			{transitionOn && (
				<SlideTop classNames="slide-in" timeout={timeout}>
					{children}
				</SlideTop>
			)}
		</TransitionGroup>
	);
};

export { SlideIn };
